import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { KeyValuePair } from '@vendasta/ai-assistants';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { switchMap } from 'rxjs/operators';
import {
  AiCapabilityForm,
  CapabilityConfigurationFormControl,
  getKeyValuePairFromCapabilityFormControl,
  setKeyValuePairFromCapabilityFormControl,
} from '../../../core/forms/ai-capability.form';
import { AiAssistantFormV2Service } from '../../ai-assistant-configuration/ai-assistant-form-v2/ai-assistant-form-v2.service';

@Component({
  selector: 'ai-capability-configuration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatInputModule, TranslateModule, GalaxyFormFieldModule],
  templateUrl: './ai-capability-configuration.component.html',
  styleUrl: './ai-capability-configuration.component.scss',
})
export class AiCapabilityConfigurationComponent {
  capabilityForm = input.required<AiCapabilityForm>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly aiAssistantFormV2Service = inject(AiAssistantFormV2Service);

  private readonly assistantTemplateVariables = toSignal(this.aiAssistantFormV2Service.assistantTemplateVariables$, {
    initialValue: new Set<string>(),
  });
  private readonly allVariables$ = toObservable(this.capabilityForm).pipe(
    switchMap((form) => form.availableVariables$),
  );
  private readonly allVariables = toSignal(this.allVariables$, { initialValue: [] });

  // Get variables from capability form and filter out existing ones
  readonly variablesToShow = computed(() => {
    return this.allVariables().filter((variable) => !this.assistantTemplateVariables().has(variable));
  });

  private readonly variableFormControls = new Map<string, FormControl<string>>();

  constructor() {
    // Watch for changes in detected variables and update form controls
    effect(() => {
      this.updateVariableFormControls();
    });
  }

  /**
   * Gets or creates a form control for a specific variable
   */
  getVariableFormControl(variableName: string): FormControl<string> {
    if (!this.variableFormControls.has(variableName)) {
      const configurationControl = this.capabilityForm().controls.configuration;
      const currentValue = getKeyValuePairFromCapabilityFormControl(configurationControl, variableName)?.value || '';

      const control = new FormControl<string>(currentValue, { nonNullable: true });

      // Watch for changes and update or remove configuration
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        if (value.trim() === '') {
          // If value is empty, remove from configuration
          this.removeVariableFromConfiguration(configurationControl, variableName);
        } else {
          // Otherwise update the configuration
          setKeyValuePairFromCapabilityFormControl(
            configurationControl,
            new KeyValuePair({ key: variableName, value }),
          );
        }
      });

      this.variableFormControls.set(variableName, control);
    }

    return this.variableFormControls.get(variableName)!;
  }

  /**
   * Removes a variable from the configuration control
   */
  private removeVariableFromConfiguration(
    configurationControl: CapabilityConfigurationFormControl,
    variableName: string,
  ): void {
    const currentConfiguration = configurationControl.value || [];
    const updatedConfiguration = currentConfiguration.filter((pair) => pair.key !== variableName);
    configurationControl.setValue(updatedConfiguration, { emitEvent: false });
  }

  /**
   * Updates all variable form controls with current configuration values
   */
  private updateVariableFormControls(): void {
    const configurationControl = this.capabilityForm().controls.configuration;

    this.variablesToShow().forEach((variableName) => {
      const control = this.variableFormControls.get(variableName);
      if (control) {
        const currentValue = getKeyValuePairFromCapabilityFormControl(configurationControl, variableName)?.value || '';
        control.setValue(currentValue, { emitEvent: false });
      }
    });

    // Remove controls AND configuration for variables that no longer exist
    const currentVariables = new Set(this.variablesToShow());
    for (const [variableName] of this.variableFormControls) {
      if (!currentVariables.has(variableName)) {
        // Remove the form control
        this.variableFormControls.delete(variableName);
        // Remove from configuration
        this.removeVariableFromConfiguration(configurationControl, variableName);
      }
    }
  }
}
